import * as React from 'react'

import { Box, Button, Href, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  AdditionalCompensationsDocument,
  AdditionalCompensationsQuery,
  ContractsTaxonomiesDocument,
} from 'generated/resources/graphql'
import { uniqueId } from 'lodash'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'

import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { compensationDefaultAttributes } from 'modules/contracts/lib/additionalCompensations'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { translate } from 'modules/contracts/lib/translate'
import Compensation from 'modules/contracts/store/models/Compensation'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'

import { ContractAdditionalCompensationList } from './ContractAdditionalCompensationList'
import AdditionalCompensationsModal from './Modal'
import { DeprecatedAdditionalCompensation } from './_AdditionalCompensation'

interface Props {
  translations: { [key: string]: string }
  step: number
  type: ContractFlowType
  isNewAdditionalCompensationsSupportEnabled?: boolean
}
interface HintProps extends Props {
  legalEntityId: number
}

const AdditionalCompensationsHint = ({ translations, legalEntityId, step, type }: HintProps) => {
  const { navigation } = useNavigation()

  const trackLink = () => {
    track(trackingEvents.employee.contracts.goToAC, {
      step: step,
      type: type,
    })
  }

  return (
    <Hint color="grey200">
      <Href
        onClick={() => trackLink()}
        href={navigation.settings.legalEntitiesId.contracts.fullPath({ id: legalEntityId })}
        target="_blank"
      >
        <Text.Normal color="grey1000" weight="normal" size="100" underlined>
          {translate(translations, 'additional_compensations_hint')}
        </Text.Normal>
      </Href>
    </Hint>
  )
}

const AdditionalCompensations = ({
  translations,
  step,
  type,
  isNewAdditionalCompensationsSupportEnabled,
}: Props) => {
  const {
    employee,
    contractVersion,
    compensations,
    setCompensations,
    contractVersionPolicy,
    additionalCompensations: additionalCompensationsList,
    setAdditionalCompensations,
  } = useContractFlow()

  const policies = usePolicies()
  const [showModal, setShowModal] = React.useState(false)

  const {
    data: additionalCompensations,
    loading,
    error,
  } = useQuery(AdditionalCompensationsDocument, {
    variables: {
      contractVersionIds: contractVersion ? [contractVersion.id] : [],
    },
    onCompleted: (data: AdditionalCompensationsQuery) => {
      const additionalCompensationsNodes = data?.contracts.additionalCompensationsConnection.nodes

      if (
        (additionalCompensationsList === null || additionalCompensationsList === undefined) &&
        additionalCompensationsNodes !== undefined
      ) {
        setAdditionalCompensations(additionalCompensationsNodes)
      }
    },
  })

  const defaultCompensation = new Compensation(
    compensationDefaultAttributes({
      contractVersion,
      policies,
      contractVersionPolicy,
    })
  )
  const [currentCompensation, setCurrentCompensation] = React.useState<Compensation | undefined>()

  const addCompensation = () => {
    setCurrentCompensation(defaultCompensation)
    setShowModal(true)
  }

  const editCompensation = (compensation: Compensation) => {
    setCurrentCompensation(compensation)
    setShowModal(true)
  }

  const onAdditionalCompensationsChange = (compensation: Compensation) => {
    let newCompensations = compensations
    if (compensation.attributes.temporal_id) {
      newCompensations = compensations.map(function (c) {
        return c.attributes.temporal_id === compensation.attributes.temporal_id ? compensation : c
      })
    } else {
      compensation.attributes.temporal_id = uniqueId()
      newCompensations.push(compensation)
    }
    setCompensations(newCompensations)
  }

  const onRemoveCompensation = (compensation: Compensation) => {
    const withoutRemovedCompensation = compensations.filter(
      (c) => c.attributes.temporal_id !== compensation.attributes.temporal_id
    )
    setCompensations(withoutRemovedCompensation)
  }

  const isCompensationEditable =
    type !== 'fix' ||
    currentCompensation?.isNew ||
    currentCompensation?.attributes.sync_with_supplements === false

  const { data } = useQuery(ContractsTaxonomiesDocument, {
    variables: {
      ids: compensations?.map((compensation) => compensation.attributes.contracts_taxonomy_id),
    },
  })

  const disabledPayrollConceptIds =
    additionalCompensations?.compensations.payrollConceptStatusesConnection.nodes
      .filter((status) => !status.enabled)
      .map((status) => status.payrollConceptId)

  const filteredPayrollConcepts =
    additionalCompensations?.payroll.conceptsConnection.nodes.filter(
      (concept) =>
        concept.name !== DEFAULT_RESULT_CONCEPT.base_salary &&
        !disabledPayrollConceptIds?.includes(concept.id)
    ) || []

  if (error) throw new Error('Error fetching data')

  return (
    <>
      {showModal && (
        <AdditionalCompensationsModal
          compensation={currentCompensation!}
          employee={employee}
          onSubmit={(compensation: Compensation) => onAdditionalCompensationsChange(compensation)}
          onRemoveCompensation={(compensation: Compensation) => onRemoveCompensation(compensation)}
          isEditable={isCompensationEditable}
          onClose={() => setShowModal(false)}
        />
      )}
      <Box gap="s24">
        <Box gap={isNewAdditionalCompensationsSupportEnabled ? 's24' : 's8'}>
          <FormWrapper>
            <Text.Normal color="grey1000" weight="normal" size="200">
              {translate(translations, 'additional_compensations')}
            </Text.Normal>
          </FormWrapper>
          <FormWrapper
            hint={
              !isNewAdditionalCompensationsSupportEnabled ? (
                <AdditionalCompensationsHint
                  translations={translations}
                  legalEntityId={employeeLegalEntityId(employee)}
                  type={type}
                  step={step}
                />
              ) : (
                ''
              )
            }
          >
            {isNewAdditionalCompensationsSupportEnabled && additionalCompensations !== undefined ? (
              loading ? (
                <Box
                  gap="s8"
                  padding="s48"
                  alignItems="center"
                  data-testid="additional-compensations-loading"
                >
                  <Spinner size="s32" data-testid="loading" />
                </Box>
              ) : (
                <ContractAdditionalCompensationList
                  additionalCompensations={additionalCompensationsList}
                  type={type}
                  payrollConcepts={filteredPayrollConcepts}
                />
              )
            ) : (
              compensations && (
                <Box gap="s8">
                  {compensations.map((compensation) => {
                    const compensationTaxonomy = data?.contracts.taxonomies.find(
                      (taxonomy) => taxonomy.id === compensation.attributes.contracts_taxonomy_id
                    )
                    return (
                      <DeprecatedAdditionalCompensation
                        employee={employee}
                        key={`compensation-${compensation.attributes.temporal_id}`}
                        onEdit={(compensation: Compensation) => editCompensation(compensation)}
                        compensation={compensation}
                        taxonomy={compensationTaxonomy}
                      />
                    )
                  })}
                </Box>
              )
            )}
          </FormWrapper>
        </Box>
        {!isNewAdditionalCompensationsSupportEnabled && (
          <Box>
            <FormWrapper>
              <Button onClick={() => addCompensation()} iconName="add">
                {translate(translations, 'add_compensation')}
              </Button>
            </FormWrapper>
          </Box>
        )}
      </Box>
    </>
  )
}

export default observer(AdditionalCompensations)
